import React, { useState, useEffect, useCallback } from 'react';
import API_STATES from '@/constants/StateConstants';
import EmptyTable from '@/components/General/EmptyTable';
import DataTable from '@/components/General/DataTable';
import * as MessageService from '@/services/api/categoryBasedNotification.service';
import generalStyles from '@/components/General/Styles.module.scss';
import styles from '../HomepageNotification.module.scss';
import deleteIcon from '@images/icons/deleteIcon.svg';
import editIcon from '@images/icons/EditIcon.svg';
import 'reactjs-popup/dist/index.css';
import moment from 'moment';
import * as Constant from '../Constants';
import { customizeDate } from '../Utils';
import Popup from 'reactjs-popup';
import ByzzerCSRModal from '@/components/ByzzerCSRModal';
import CategoryBasedNotificationForm from './CategoryBasedNotificationForm';

function CategoryBasedNotificationDetailedHistory({ refreshHistory, refresh, selectedNotification }) {
    const [userNotificationApiStatus, setUserNotificationApiStatus] = useState(API_STATES.none);
    const [userNotificationApiError, setUserNotificationApiError] = useState('');
    const [userNotificationList, setUserNotificationList] = useState([]);
    const [userNotificationDeleteStatus, setUserNotificationDeleteStatus] = useState(API_STATES.none);
    const [userNotificationDeleteError, setUserNotificationDeleteError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [selectedRowData, setSelectedRowData] = useState({});
    const [edit, setEdit] = useState(false);

    const COLUMNS = [
        {
            Header: 'Notification ID',
            accessor: 'notificationId',
            className: generalStyles.tableCell,
        },
        {
            Header: 'Companies',
            accessor: 'company',
            className: generalStyles.tableCell,
        },
        {
            Header: 'User',
            accessor: 'email',
            className: generalStyles.tableCell,
        },
        {
            Header: 'Expiration date',
            accessor: 'endDate',
            className: generalStyles.tableCell,
        },
        {
            Header: 'Type of Notification',
            accessor: 'notificationType',
            className: generalStyles.tableCell,
        },
        {
            Header: 'User Action',
            accessor: 'userAction',
            className: generalStyles.tableCell,
        },
        {
            Header: 'Date of User Action',
            accessor: 'userActionDate',
            className: generalStyles.tableCell,
        },
        {
            Header: 'Action',
            accessor: (row) => row,
            id: 'actions',
            disableFilters: true,
            disableSortBy: true,
            className: generalStyles.tableCell,
            Cell: ({ value: row }) => {
                const isExpired = moment(row.expiration).isAfter(moment(new Date()));
                return (
                    <div className={styles.actionContainer}>
                        <div
                            className={styles.expandIconContainer}
                            onClick={
                                isExpired
                                    ? () => {
                                          deleteClicked(row);
                                      }
                                    : {}
                            }
                        >
                            <img
                                src={deleteIcon}
                                alt="delete"
                                className={isExpired ? styles.btnWidth : styles.disabledIcon}
                            />
                        </div>
                        <div
                            className={styles.expandIconContainer}
                            onClick={() => {
                                editClicked(row);
                            }}
                        >
                            <img src={editIcon} alt="edit" className={styles.btnWidth} />
                        </div>
                    </div>
                );
            },
        },
    ];

    const getUserNotificationHistory = useCallback(async () => {
        if (selectedNotification) {
            refresh(false);
            setUserNotificationApiStatus(API_STATES.loading);
            let response = await MessageService.getNotificationDetail(
                Constant.MESSAGE_TYPE.CATEGORY_BASED,
                selectedNotification.notificationId
            );
            if (response.status === API_STATES.success) {
                const result = response.data;
                setUserNotificationApiStatus(API_STATES.success);
                setUserNotificationList(result.data);
            } else {
                setUserNotificationApiError(response.error);
                setUserNotificationList([]);
                setUserNotificationApiStatus(API_STATES.error);
            }
        } else return false;
    }, [refresh, selectedNotification]);

    useEffect(() => {
        if (refreshHistory) {
            getUserNotificationHistory();
        }
    }, [refreshHistory, getUserNotificationHistory]);

    const transformTabelColumns = (list) => {
        const historyData = [];
        let type = {};
        let userAction;
        let userActionDate;

        for (const data of list) {
            type = Constant.NOTIFICATION_TYPE.filter((item) => {
                return item.value === data.messageType;
            });
            userAction = '';
            userActionDate = '';
            // checking the latest action performed by the user
            if (data.isDone === true) {
                userAction = Constant.USER_ACTIONS.ALREADY_DONE;
                userActionDate = customizeDate(data.readDtm);
            } else if (data.isDeleted === true && data.deletedDtm !== null) {
                userAction = Constant.USER_ACTIONS.DISMISS;
                userActionDate = customizeDate(data.deletedDtm);
            } else if (data.readDtm !== null && data.snoozeDtm === null) {
                userAction = Constant.USER_ACTIONS.DONE;
                userActionDate = customizeDate(data.readDtm);
            } else if (data.readDtm === null && data.snoozeDtm !== null) {
                userAction = Constant.USER_ACTIONS.DO_LATER;
                userActionDate = customizeDate(data.snoozeDtm);
            } else if (data.readDtm !== null && data.snoozeDtm !== null) {
                if (moment(new Date(data.readDtm)).isAfter(moment(new Date(data.snoozeDtm)))) {
                    userAction = Constant.USER_ACTIONS.DONE;
                    userActionDate = customizeDate(data.readDtm);
                } else {
                    userAction = Constant.USER_ACTIONS.DO_LATER;
                    userActionDate = customizeDate(data.snoozeDtm);
                }
            }

            let dataObj = {
                id: data.id,
                notificationId: data.notificationId,
                endDate: customizeDate(data.endDate),
                notificationType: data.messageType,
                type: type,
                expiration: data.endDate,
                company: data.company,
                user: data.firstName + ' ' + data.lastName,
                email: data.email,
                userAction: userAction,
                userActionDate: userActionDate,
                csrNotificationId: data.csrConfigId,
                marketNotificationType: data.criteria?.notificationType,
                recepientId: data.recepientId,
                companyId: data.company_id,
            };
            historyData.push(dataObj);
        }
        return historyData;
    };

    function deleteClicked(rowData) {
        setSelectedRowData(rowData);
        setOpenModal(true);
    }

    function editClicked(rowData) {
        const newRowData = {
            ...rowData,
            message: selectedNotification?.message,
            header: selectedNotification?.header,
            hyperLink: selectedNotification?.hyperLink,
            runType: selectedNotification?.runType,
            categoryList: selectedNotification?.categoryList,
            categories: selectedNotification?.categories,
            timeRange: selectedNotification?.timeRange,
            fromDate: selectedNotification?.fromDate,
            toDate: selectedNotification?.toDate,
        };
        setSelectedRowData(newRowData);
        setEdit(true);
    }

    function closeEdit() {
        setEdit(false);
        setSelectedRowData(null);
    }

    function getTerminateReqBody() {
        return {
            id: selectedRowData.id,
            recepientId: selectedRowData.recepientId,
            notificationId: selectedRowData.notificationId,
            companyId: selectedRowData.companyId,
            expirationDtm: moment(new Date()).startOf('day').format('YYYY/MM/DD HH:mm:ss'),
            notification: {
                message: selectedNotification?.message,
                header: selectedNotification?.header,
                hyperLink: selectedNotification?.hyperLink,
            },
            categoryMessage: {
                categories: selectedNotification?.categoryList,
                categoryTriggerType: selectedNotification.runType,
                startDtm: selectedNotification.fromDate,
                endDtm: selectedNotification.toDate,
            },
            messageType: selectedRowData.notificationType,
            triggerType: Constant.MANUAL,
            priority: Constant.PRIORITY.LOW,
            messageCreationType: Constant.MESSAGE_TYPE.CATEGORY_BASED,
        };
    }

    async function terminateNotification() {
        try {
            setOpenModal(false);
            if (selectedRowData) {
                setUserNotificationDeleteStatus(API_STATES.loading);
                const requestBody = getTerminateReqBody();
                let response = await MessageService.editCompanyBasedMessage(requestBody);
                if (response.status === API_STATES.success) {
                    setUserNotificationDeleteStatus(API_STATES.success);
                    refresh(true);
                } else {
                    setUserNotificationDeleteError(response.error);
                    setUserNotificationDeleteStatus(API_STATES.error);
                    return false;
                }
            } else {
                return false;
            }
        } catch (err) {
            setUserNotificationDeleteError(err);
            setUserNotificationDeleteStatus(API_STATES.error);
        }
    }

    return (
        <>
            <h3>Detailed Notification History</h3>
            <br />
            <div>
                {userNotificationApiStatus === API_STATES.error && (
                    <p className={generalStyles.errorMessage}>Error: {userNotificationApiError}</p>
                )}
                {userNotificationApiStatus === API_STATES.none && (
                    <p className={generalStyles.statusMessage}>Loading...</p>
                )}
                {userNotificationApiStatus === API_STATES.loading && (
                    <p className={generalStyles.statusMessage}>Loading...</p>
                )}
                {userNotificationApiStatus === API_STATES.success && (
                    <>
                        {userNotificationList.length > 0 ? (
                            <DataTable
                                data={transformTabelColumns(userNotificationList)}
                                columns={COLUMNS}
                                withDownload
                                downloadFileName={'CategoryBasedNotificationsDetailedHistory'}
                            />
                        ) : (
                            <div>
                                <EmptyTable
                                    noValueDescription="No Notification History found."
                                    columns={[
                                        'Notification ID',
                                        'Flagged Category',
                                        'Report/Subscription based',
                                        'Time Range',
                                        'Companies',
                                        'User',
                                        'Expiration date',
                                        'Type of Notification',
                                        'Notification Content',
                                        'Link',
                                        'User Action',
                                        'Date of User Action',
                                        'Action',
                                    ]}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>

            {openModal && (
                <Popup
                    open={openModal}
                    onClose={() => {
                        setOpenModal(false);
                    }}
                    modal
                >
                    {() => (
                        <div className={styles.warningDialog}>
                            <h2>Warning!</h2>
                            <div>
                                <div>
                                    {' '}
                                    <b>Are you sure you want to terminate the Notification for this user?</b>
                                </div>
                                <br></br>
                                <button
                                    className={`${generalStyles.confirmOperationButton} ${styles.btnWidth}`}
                                    onClick={() => {
                                        terminateNotification();
                                    }}
                                >
                                    Terminate for single user
                                </button>
                                <button
                                    className={`${generalStyles.cancelOperationButton} ${styles.btnWidth}`}
                                    onClick={() => {
                                        setOpenModal(false);
                                    }}
                                >
                                    Cancel
                                </button>{' '}
                            </div>
                        </div>
                    )}
                </Popup>
            )}

            {edit && (
                <ByzzerCSRModal
                    show={true}
                    onClose={() => setEdit(false)}
                    size={'extra-large'}
                    type={'info'}
                    headerType={'none'}
                    children={
                        <CategoryBasedNotificationForm
                            selectedNotification={selectedRowData}
                            edit={edit}
                            refresh={(flag) => refresh(flag)}
                            closeEdit={() => closeEdit()}
                            editSingleNotification={edit}
                        />
                    }
                />
            )}
        </>
    );
}
export default CategoryBasedNotificationDetailedHistory;
